import { COLLABORATION_METRICS_MAX_DAYS } from 'constants/appConstants';

import type {
  CollaborationMetricsDataPointType,
  CollaborationMetricsRawMetricType,
  CollaborationMetricsRawRecordType,
  CollaborationMetricsStackedChartModelType,
  CollaborationMetricsStackedSeriesEntryType,
  TTSCollaborationMetricsType
} from 'model/types/collaboration-metrics';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Calendar day key (YYYY-MM-DD) for aligning stacked series. */
function toComparableDateKey(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) {
    const t = Date.parse(value);
    if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e11 ? value * 1000 : value;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  return null;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.length > 0 && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/** Raw snapshot date for “Data updated on” (must be parseable by `Date`). */
function recordDateToRawString(value: unknown): string | null {
  const numeric = toFiniteNumber(value);

  if (numeric !== null) {
    const ms = numeric < 1e11 ? numeric * 1000 : numeric;
    return new Date(ms).toISOString();
  }

  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  return null;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function normalizeSeriesFromPointRows(rows: readonly unknown[]): CollaborationMetricsDataPointType[] {
  const out: CollaborationMetricsDataPointType[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const dateKey = toComparableDateKey(row.date);
    if (dateKey == null) continue;
    out.push({ date: dateKey, value: toNumber(row.value) });
  }
  return out;
}

function normalizeSeriesFromParallelArrays(raw: Record<string, unknown>): CollaborationMetricsDataPointType[] {
  const dates = raw.dates;
  const values = raw.values;
  if (!Array.isArray(dates) || !Array.isArray(values)) return [];
  const len = Math.min(dates.length, values.length);
  const out: CollaborationMetricsDataPointType[] = [];
  for (let i = 0; i < len; i += 1) {
    const dateKey = toComparableDateKey(dates[i]);
    if (dateKey == null) continue;
    out.push({ date: dateKey, value: toNumber(values[i]) });
  }
  return out;
}

function normalizeSeries(raw: unknown): CollaborationMetricsDataPointType[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return normalizeSeriesFromPointRows(raw);
  if (isRecord(raw)) return normalizeSeriesFromParallelArrays(raw);
  return [];
}

function valueOnDate(points: CollaborationMetricsDataPointType[], date: string): number {
  const hit = points.find((p) => p.date === date);
  return hit?.value ?? 0;
}

function buildStackedModel(metric: CollaborationMetricsRawMetricType | undefined): CollaborationMetricsStackedChartModelType | null {
  if (!metric) return null;
  const open = normalizeSeries(metric.open);
  const target = normalizeSeries(metric.target);
  const partner = normalizeSeries(metric.partner);
  const dateSet = new Set<string>();
  for (const p of open) dateSet.add(p.date);
  for (const p of target) dateSet.add(p.date);
  for (const p of partner) dateSet.add(p.date);
  const sorted = [...dateSet].sort((a, b) => a.localeCompare(b));
  const last = sorted.slice(-COLLABORATION_METRICS_MAX_DAYS);
  if (last.length === 0) return null;

  const dataOpen = last.map((d) => valueOnDate(open, d));
  const dataTarget = last.map((d) => valueOnDate(target, d));
  const dataPartner = last.map((d) => valueOnDate(partner, d));

  const series: CollaborationMetricsStackedSeriesEntryType[] = [
    { name: 'open', data: dataOpen },
    { name: 'target', data: dataTarget },
    { name: 'partner', data: dataPartner }
  ];

  return {
    categories: last,
    series
  };
}

/**
 * Keeps only the last N daily buckets (aligned across categories and each stacked series).
 */
export function sliceCollaborationStackedChartModel(
  model: CollaborationMetricsStackedChartModelType | null,
  lastNDays: number
): CollaborationMetricsStackedChartModelType | null {
  if (!model || model.categories.length === 0) return model;
  const cap = Math.max(1, Math.floor(lastNDays));
  const take = Math.min(cap, model.categories.length);
  const start = model.categories.length - take;
  const categories = model.categories.slice(start);
  const series: CollaborationMetricsStackedSeriesEntryType[] = model.series.map((s) => ({
    name: s.name,
    data: s.data.slice(start)
  }));
  return { categories, series };
}

/**
 * Reads `tts_txdata_record` from merchant metadata JSON and builds stacked bar models
 * (up to {@link COLLABORATION_METRICS_MAX_DAYS} days) for Video GMV and Video Posts.
 *
 * Accepts `metadata` as a JSON string or as an already-parsed object (GraphQL JSON scalars).
 */
/** Turns {@link TTSCollaborationMetricsType.updatedAtRaw} into a valid `Date`, or `null`. */
export function parseCollaborationMetricsUpdatedAtDate(updatedAtRaw: string | null): Date | null {
  if (updatedAtRaw == null) return null;
  const d = new Date(updatedAtRaw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function parseCollaborationMetricsFromMetadata(metadata: string | undefined): TTSCollaborationMetricsType {
  const empty: TTSCollaborationMetricsType = {
    updatedAtRaw: null,
    videoGmv: null,
    videoPosts: null
  };

  if (metadata == null || metadata === '') {
    return empty;
  }

  const root = JSON.parse(metadata);
  if (!root) return empty;

  const recordUnknown = root.tts_txdata_record;
  if (!isRecord(recordUnknown)) return empty;
  const record = recordUnknown as CollaborationMetricsRawRecordType;

  const updatedAtRaw = recordDateToRawString(record.date);
  const txData = isRecord(record.txData) ? record.txData : undefined;

  return {
    updatedAtRaw,
    videoGmv: buildStackedModel(txData?.affiliate_gmv),
    videoPosts: buildStackedModel(txData?.promoting_creators_cnt)
  };
}
